import React, { useState, useEffect } from 'react';
import CustomFieldInputs, { useIntakeCustomFields } from '../CustomFields/CustomFieldInputs';
import { customerAPI } from '../../services/api';
import AddressAutocomplete from '../AddressAutocomplete/AddressAutocomplete';
import useZipLookup from '../../utils/useZipLookup';
import { formatPhone, formatPhoneInput, phoneForSave } from '../../utils/phone';
import './customerModal.css';

// Every field on this form that holds a phone number, so handleChange can format
// as the user types without each input having to opt in.
const PHONE_FIELDS = new Set(['phone', 'mobilePhone', 'mainPhone', 'workPhone']);

// Mirrors ContactType on the backend. Type is stored as an int.
const CONTACT_TYPES = [
    { value: 0, label: 'Homeowner' },
    { value: 1, label: 'Property Manager' },
    { value: 2, label: 'Subcontractor' },
    { value: 3, label: 'Supplier' },
    { value: 4, label: 'Inspector' },
    { value: 5, label: 'Adjuster' },
    { value: 6, label: 'Other' },
    { value: 7, label: 'Tenant' },
];

// Mirrors ContactEntityKind on the backend.
const ENTITY_KINDS = [
    { value: 0, label: 'Person' },
    { value: 1, label: 'Company' },
    { value: 2, label: 'Complex' },
];

const blankForm = {
    type: 0,
    entityKind: 0,
    firstName: '',
    lastName: '',
    displayName: '',
    companyName: '',
    email: '',
    phone: '',
    mainPhone: '',
    mobilePhone: '',
    workPhone: '',
    // Property address (where work happens, or primary address for non-homeowners)
    propertyStreet: '',
    addressLine2: '',
    propertyCity: '',
    propertyState: 'TN',
    propertyZipCode: '',
    // Billing address (homeowners only — usually same as property)
    billingSameAsProperty: true,
    billingStreet: '',
    billingCity: '',
    billingState: 'TN',
    billingZipCode: '',
    gateCode: '',
    description: '',
    notes: '',
};

const trimOrNull = (v) => {
    const s = String(v ?? '').trim();
    return s === '' ? null : s;
};

// The name a person's Display Name would auto-fill to. Also the yardstick for
// deciding whether a stored display name is a deliberate override.
const autoDisplayName = (c) =>
    [c?.firstName, c?.lastName].filter(Boolean).join(' ').trim();

// An API contact row → form state. Shared by editing and by seeding a new
// contact from an existing one ("Duplicate"), so the two can't drift apart.
const customerToForm = (c) => {
    const samePropAsBilling =
        (c.billingStreet || '') === (c.propertyStreet || '') &&
        (c.billingCity || '') === (c.propertyCity || '') &&
        (c.billingState || '') === (c.propertyState || '') &&
        (c.billingZipCode || '') === (c.propertyZipCode || '');
    return {
        type: c.type ?? 0,
        entityKind: c.entityKind ?? 0,
        firstName: c.firstName || '',
        lastName: c.lastName || '',
        displayName: c.displayName || '',
        companyName: c.companyName || '',
        email: c.email || '',
        // formatPhone, not formatPhoneInput: it hands back anything that isn't a
        // plain 10-digit number untouched, so an imported extension survives being
        // opened and saved again.
        phone: formatPhone(c.phone || ''),
        mainPhone: formatPhone(c.mainPhone || ''),
        mobilePhone: formatPhone(c.mobilePhone || ''),
        workPhone: formatPhone(c.workPhone || ''),
        propertyStreet: c.propertyStreet || '',
        addressLine2: c.addressLine2 || '',
        propertyCity: c.propertyCity || '',
        propertyState: c.propertyState || '',
        propertyZipCode: c.propertyZipCode || '',
        // If billing fields are empty OR match property, treat as "same as property".
        // Otherwise show them as a separate block for editing.
        billingSameAsProperty: !c.billingStreet || samePropAsBilling,
        billingStreet: c.billingStreet || '',
        billingCity: c.billingCity || '',
        billingState: c.billingState || '',
        billingZipCode: c.billingZipCode || '',
        gateCode: c.gateCode || '',
        description: c.description || '',
        notes: c.notes || '',
    };
};

// `seedCustomer` prefills a *new* contact from an existing one ("Duplicate").
// Unlike `editingCustomer` it never becomes an update — submitting always creates.
const CustomerModal = ({ isOpen, onClose, onCustomerCreated, editingCustomer = null, seedCustomer = null }) => {
    const [formData, setFormData] = useState(blankForm);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Display Name mirrors the name fields until someone writes their own — after
    // that it's theirs and typing a name must not overwrite it.
    const [displayNameTouched, setDisplayNameTouched] = useState(false);

    const isEditing = !!editingCustomer;

    // Contact custom fields marked "Intake". Same hook the job form uses, so a field
    // Trey adds on the Contacts tab of the admin page shows up here with no deploy.
    const customFields = useIntakeCustomFields('Contact', isOpen, editingCustomer?.id);

    useEffect(() => {
        if (!isOpen) return;
        const source = editingCustomer || seedCustomer;
        setFormData(source ? customerToForm(source) : blankForm);
        // A stored display name that's just "First Last" is the auto-fill, not an
        // override, so it stays in step with further edits to the name.
        setDisplayNameTouched(
            !!source?.displayName?.trim() &&
            source.displayName.trim() !== autoDisplayName(source)
        );
        setError('');
    }, [isOpen, editingCustomer, seedCustomer]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Compose billing fields: if the toggle is on, mirror property; else use the
            // separately entered values.
            const billing = formData.billingSameAsProperty
                ? {
                      billingStreet: trimOrNull(formData.propertyStreet),
                      billingCity: trimOrNull(formData.propertyCity),
                      billingState: trimOrNull(formData.propertyState),
                      billingZipCode: trimOrNull(formData.propertyZipCode),
                  }
                : {
                      billingStreet: trimOrNull(formData.billingStreet),
                      billingCity: trimOrNull(formData.billingCity),
                      billingState: trimOrNull(formData.billingState),
                      billingZipCode: trimOrNull(formData.billingZipCode),
                  };

            const customerData = {
                type: parseInt(formData.type, 10),
                entityKind: parseInt(formData.entityKind, 10),
                firstName: trimOrNull(formData.firstName),
                lastName: trimOrNull(formData.lastName),
                displayName: trimOrNull(formData.displayName),
                companyName: trimOrNull(formData.companyName),
                email: trimOrNull(formData.email),
                phone: trimOrNull(phoneForSave(formData.phone)),
                mainPhone: trimOrNull(phoneForSave(formData.mainPhone)),
                mobilePhone: trimOrNull(phoneForSave(formData.mobilePhone)),
                workPhone: trimOrNull(phoneForSave(formData.workPhone)),
                propertyStreet: trimOrNull(formData.propertyStreet),
                addressLine2: trimOrNull(formData.addressLine2),
                propertyCity: trimOrNull(formData.propertyCity),
                propertyState: trimOrNull(formData.propertyState),
                propertyZipCode: trimOrNull(formData.propertyZipCode),
                ...billing,
                gateCode: trimOrNull(formData.gateCode),
                description: trimOrNull(formData.description),
                notes: trimOrNull(formData.notes),
            };


            // The created id is needed for custom field values, which cannot be
            // written until the contact exists.
            let contactId = editingCustomer?.id;
            if (isEditing) {
                await customerAPI.update(editingCustomer.id, { ...customerData, id: editingCustomer.id });
            } else {
                const created = await customerAPI.create(customerData);
                contactId = created.data?.id;
            }

            await customFields.save(contactId);

            onCustomerCreated();
            onClose();
            if (!isEditing) setFormData(blankForm);
        } catch (err) {
            let msg = isEditing ? 'Failed to update contact' : 'Failed to create contact';
            if (err.response?.data?.errors) {
                const allErrors = Object.entries(err.response.data.errors).map(([field, messages]) =>
                    `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
                );
                msg = allErrors.join('; ');
            } else if (err.response?.data?.title) {
                msg = err.response.data.title;
            }
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const lookupPropertyZip = useZipLookup(React.useCallback(({ city, state }) => {
        setFormData(prev => ({
            ...prev,
            ...(city ? { propertyCity: city } : {}),
            ...(state ? { propertyState: state } : {}),
        }));
    }, []));

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const next = type === 'checkbox' ? checked
            : PHONE_FIELDS.has(name) ? formatPhoneInput(value)
            : value;

        // Same rule as the job site: five digits imply a city and a state.
        if (name === 'propertyZipCode') {
            lookupPropertyZip(value, formData.propertyCity, formData.propertyState);
        }

        setFormData((prev) => {
            const updated = { ...prev, [name]: next };
            if ((name === 'firstName' || name === 'lastName') && !displayNameTouched) {
                updated.displayName = autoDisplayName(updated);
            }
            return updated;
        });

        // Clearing the field opts back into auto-fill, so a bad override is easy
        // to undo without reopening the contact.
        if (name === 'displayName') setDisplayNameTouched(next.trim() !== '');
    };

    const handleClose = () => {
        onClose();
        if (!isEditing) setFormData(blankForm);
    };

    if (!isOpen) return null;

    const isHomeowner = parseInt(formData.type, 10) === 0;

    return (
        // No click-to-close on the backdrop — a stray click outside used to throw
        // away everything typed. Closing is the X and Cancel only.
        <div className="modal-overlay">
            <div className="modal-content customer-modal">
                <div className="modal-header">
                    <h2 className="modal-title">
                        {isEditing ? 'Edit Contact' : seedCustomer ? 'Duplicate Contact' : 'Add New Contact'}
                    </h2>
                    <button className="modal-close" onClick={handleClose}>×</button>
                </div>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit} className="customer-form">
                    {/* Classification */}
                    <div className="form-section">
                        <h3 className="section-title">Classification</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Type *</label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    required
                                    className="form-input"
                                >
                                    {CONTACT_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Entity Kind</label>
                                <select
                                    name="entityKind"
                                    value={formData.entityKind}
                                    onChange={handleChange}
                                    className="form-input"
                                >
                                    {ENTITY_KINDS.map((k) => (
                                        <option key={k.value} value={k.value}>{k.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Identifying Info */}
                    <div className="form-section">
                        <h3 className="section-title">Name &amp; Company</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">First Name</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="(optional for companies)"
                                    maxLength={100}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Last Name</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="(optional for companies)"
                                    maxLength={100}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Company Name</label>
                                <input
                                    type="text"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="e.g., Ghertner & Company"
                                    maxLength={200}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Display Name</label>
                                <input
                                    type="text"
                                    name="displayName"
                                    value={formData.displayName}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="Fills in from the name — type to override"
                                    maxLength={300}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Contact Info */}
                    <div className="form-section">
                        <h3 className="section-title">Contact Info</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="name@example.com"
                                    maxLength={200}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Primary Phone</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="(615) 555-1234"
                                    maxLength={50}
                                />
                            </div>
                        </div>
                        {/* Two phone fields, not four.
                            JobNimbus's Main/Mobile/Work layout came over on import and
                            nobody filled it consistently, so which number an automation
                            texted depended on which field happened to be populated. Of
                            553 contacts, 355 held the SAME number duplicated across
                            fields and only 66 held more than one real number.
                            mainPhone/workPhone are no longer edited here, but the
                            columns keep their values — four contacts hold a third
                            distinct number that would otherwise be lost. */}
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Secondary Phone</label>
                                <input
                                    type="tel"
                                    name="mobilePhone"
                                    value={formData.mobilePhone}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="(optional)"
                                    maxLength={50}
                                />
                            </div>
                            {(formData.mainPhone || formData.workPhone) && (
                                <div className="form-group">
                                    <label className="form-label">Other numbers on file</label>
                                    <div className="legacy-phone-note">
                                        {[formData.mainPhone, formData.workPhone].filter(Boolean).join(' · ')}
                                        <span> — kept from the import, no longer edited here.</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Address */}
                    <div className="form-section">
                        <h3 className="section-title">
                            {isHomeowner ? 'Property Address' : 'Primary Address'}
                        </h3>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Street Address</label>
                                <AddressAutocomplete
                                    value={formData.propertyStreet}
                                    onChange={(v) => handleChange({ target: { name: 'propertyStreet', value: v } })}
                                    onSelect={(a) => setFormData(prev => ({
                                        ...prev,
                                        propertyStreet: a.street || prev.propertyStreet,
                                        propertyCity: a.city || prev.propertyCity,
                                        propertyState: a.state || prev.propertyState,
                                        propertyZipCode: a.zip || prev.propertyZipCode,
                                    }))}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Address Line 2</label>
                                <input
                                    type="text"
                                    name="addressLine2"
                                    value={formData.addressLine2}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="Suite, unit, etc. (optional)"
                                    maxLength={200}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">City</label>
                                <input
                                    type="text"
                                    name="propertyCity"
                                    value={formData.propertyCity}
                                    onChange={handleChange}
                                    className="form-input"
                                    maxLength={100}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">State</label>
                                <input
                                    type="text"
                                    name="propertyState"
                                    value={formData.propertyState}
                                    onChange={handleChange}
                                    className="form-input"
                                    maxLength={50}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Zip</label>
                                <input
                                    type="text"
                                    name="propertyZipCode"
                                    value={formData.propertyZipCode}
                                    onChange={handleChange}
                                    className="form-input"
                                    maxLength={20}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Gate Code</label>
                                <input
                                    type="text"
                                    name="gateCode"
                                    value={formData.gateCode}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="(if needed)"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Billing address — only shown when "same as property" is off. */}
                    {isHomeowner && (
                        <div className="form-section">
                            <h3 className="section-title">Billing Address</h3>
                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label className="form-checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="billingSameAsProperty"
                                            checked={formData.billingSameAsProperty}
                                            onChange={handleChange}
                                            className="form-checkbox"
                                        />
                                        Same as property address
                                    </label>
                                </div>
                            </div>
                            {!formData.billingSameAsProperty && (
                                <>
                                    <div className="form-row">
                                        <div className="form-group full-width">
                                            <label className="form-label">Street Address</label>
                                            <input
                                                type="text"
                                                name="billingStreet"
                                                value={formData.billingStreet}
                                                onChange={handleChange}
                                                className="form-input"
                                                maxLength={200}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label className="form-label">City</label>
                                            <input
                                                type="text"
                                                name="billingCity"
                                                value={formData.billingCity}
                                                onChange={handleChange}
                                                className="form-input"
                                                maxLength={100}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">State</label>
                                            <input
                                                type="text"
                                                name="billingState"
                                                value={formData.billingState}
                                                onChange={handleChange}
                                                className="form-input"
                                                maxLength={50}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Zip</label>
                                            <input
                                                type="text"
                                                name="billingZipCode"
                                                value={formData.billingZipCode}
                                                onChange={handleChange}
                                                className="form-input"
                                                maxLength={20}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Notes */}
                    <div className="form-section">
                        <h3 className="section-title">Notes</h3>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    className="form-textarea"
                                    rows="2"
                                    placeholder="Short description (optional)"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Notes</label>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    className="form-textarea"
                                    rows="3"
                                    placeholder="Longer-form notes (optional)"
                                />
                            </div>
                        </div>
                    </div>

                    <CustomFieldInputs {...customFields} />

                    <div className="modal-actions">
                        <button type="button" onClick={handleClose} className="btn-cancel">Cancel</button>
                        <button type="submit" disabled={loading} className="btn-primary">
                            {loading
                                ? (isEditing ? 'Updating...' : 'Creating...')
                                : (isEditing ? 'Update Contact' : 'Add Contact')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CustomerModal;
